/**
 * MockExamView (P360) -- rehearse the SAMA field exam (killer-features
 * F2). Route: /admin/mock-exam.
 *
 * The deterministic report renders on any deployment (Engine-off
 * included); the "Run the mock exam" button adds the Engine's
 * per-question examiner working-paper notes (grounded + sealed),
 * polling the shared batch substrate while the pass runs.
 */
import { useState } from "react";
import { Arrow } from "shared/components/Arrow";
import { useEffect, useRef } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { entityDetailPath } from "shared/routes/entityRoutes";
import { formatDateTime } from "shared/time/formatDate";

import { api, currentAuthToken } from "shared/api/client";
import type { EngineBatchRun } from "shared/api/types";
import { MarsadIcon } from "shared/components/MarsadIcon";
import { modelsDifferSentence } from "shared/engine/modelSentence";
import { useToast } from "shared/toast/ToastProvider";
import { EngineLanguageNote } from "shared/components/EngineLanguageNote";

interface ExamNarrative {
  note: string;
  citations: string[];
  invented_dropped: number;
  /* Round 12 D4 -- figures the note states that its own evidence does
     not. Nine of thirty-two notes carried one; "98.2% mean coverage"
     printed as "100% coverage and full compliance". */
  unsupported_figures?: string[];
  /* Round 13 F3 -- three checks that catch what a numeral check
     structurally cannot. Eight of twenty-five notes were wrong in a
     way an examiner would have to correct and none of the eight
     carried an unsupported figure. */
  contradicts_verdict?: string | null;
  reused_ratios?: string[];
  /* Round 15 A13 -- a grading method the evidence never names. */
  invented_method?: string | null;
  worse_uncited_evidence?: string[];
  /* Round 13 F2 -- the deterministic half is recomputed on every load
     while the narration is frozen, so the page printed a note reading
     "11 of 36 critical" against an evidence row reading "12 of 37" and
     the counter flagged neither: at the moment of the check they
     agreed. */
  checked_at?: string | null;
  evidence_moved?: boolean;
}

interface ExamQuestionOut {
  id: string;
  area: string;
  question: string;
  evidence: { id: string; text: string }[];
  status: "ok" | "flagged" | "not_assessed";
  severity: "high" | "medium" | "info";
  likely_finding: string | null;
  /* Round 12 F7 -- the entity this row is ABOUT, where it has one. The
     page carried zero content links on twenty rows that each name an
     entity in their own sentence. */
  entity_kind?: string | null;
  entity_id?: string | null;
  narrative?: ExamNarrative;
  /* ROUND 15 A10 -- the Engine was asked about this row and did not
     answer. Distinct from a row the script derived after the run. */
  narrative_refused?: boolean;
}

interface MockExamReport {
  report_kind: string;
  generated_at: string;
  questions: ExamQuestionOut[];
  likely_findings: ExamQuestionOut[];
  summary: {
    questions: number;
    flagged: number;
    /** Round 19 B27 -- the questions nothing could judge, and those passing. */
    not_assessed?: number;
    passing?: number;
    high: number;
    narrated: number;
    notes_with_unsupported_figures?: number;
    /* Round 13 F3 -- everything worth a second read, counted once. */
    notes_flagged_for_review?: number;
    /* Round 13 F2 -- notes whose evidence has moved under them. */
    notes_whose_evidence_moved?: number;
  };
  narrative_run: {
    run_id: string;
    model: string | null;
    /* Round 13 F4 -- what the deployment ASKED for, beside what
       answered, and whether the two disagree. The composer has
       computed `model_disagrees` server-side since round 12 and this
       view did not read it, so "the mock exam prints the
       disagreement" was not satisfied. */
    configured_model?: string | null;
    model_observed?: boolean;
    model_disagrees?: boolean;
    finished_at: string | null;
    /** Round 16 A26 -- how many questions that run was asked. */
    questions_asked?: number | null;
  } | null;
}

const SEV_CLASS: Record<string, string> = {
  high: "bg-rose-100 text-rose-800",
  medium: "bg-amber-100 text-amber-800",
  info: "bg-slate-100 text-slate-600",
};

async function downloadPack(runId: string | null = null): Promise<void> {
  const token = currentAuthToken();
  // Round 19 A41 -- a past run's pack is that run as it stood.
  const query = runId ? `?run_id=${encodeURIComponent(runId)}` : "";
  const response = await fetch(`/v1/admin/exports/mock-exam-pack.zip${query}`, {
    headers: {
      Accept: "application/zip",
      ...(token ? { Authorization: `Bearer ${token}` } : {}),
    },
  });
  if (!response.ok) throw new Error(`HTTP ${response.status}`);
  const blob = await response.blob();
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = "marsad-mock-exam-pack.zip";
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  setTimeout(() => URL.revokeObjectURL(url), 1_000);
}

/** Round 13 F3 -- is this note worth a second read?
 *
 * Round 12 D4 asked only about figures, and eight of twenty-five wrong
 * notes carried none: the worst read an evidence row saying "5 of 7
 * breach this standard" as "supporting 5 of 7 services". One predicate
 * so the heading, the tint and the count cannot disagree about which
 * notes are flagged.
 */
function needsReview(n: ExamNarrative): boolean {
  return (
    (n.unsupported_figures?.length ?? 0) > 0 ||
    Boolean(n.contradicts_verdict) ||
    (n.reused_ratios?.length ?? 0) > 0 ||
    Boolean(n.invented_method) ||
    (n.worse_uncited_evidence?.length ?? 0) > 0
  );
}

function QuestionRow({ q }: { q: ExamQuestionOut }) {
  const { t, i18n } = useTranslation();
  const [open, setOpen] = useState(false);
  return (
    <li
      data-testid={`mock-exam-q-${q.id}`}
      className={`rounded border px-3 py-2 text-sm ${
        q.status === "flagged"
          ? "border-rose-200 bg-rose-50/50"
          : "border-paper-2 bg-white"
      }`}
    >
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        className="flex w-full flex-wrap items-baseline gap-x-2 text-start"
      >
        <span
          className={`rounded px-1.5 py-0.5 text-xs font-semibold uppercase ${SEV_CLASS[q.severity] ?? SEV_CLASS.info}`}
        >
          {q.severity}
        </span>
        <span className="font-mono text-[11px] uppercase text-slate-400">{q.area}</span>
        <span className="font-medium text-slate-900">
          {q.likely_finding ?? q.question}
        </span>
        <span className="ms-auto text-xs text-brand-brassDark">
          {open ? t("mockExam.hide") : t("mockExam.detail")}
        </span>
      </button>
      {/* Round 12 F7 -- the row names an entity in its own sentence and
          offered no way to open it. An examiner's first question is
          always WHICH, and his second is to look. Only where the row
          HAS one: a question about a register has no row to open, and
          inventing a destination is worse than offering none. */}
      {q.entity_kind && q.entity_id && entityDetailPath(q.entity_kind, q.entity_id) && (
        <Link
          to={entityDetailPath(q.entity_kind, q.entity_id) as string}
          data-testid={`mock-exam-open-${q.id}`}
          className="mt-1 inline-block text-xs font-medium text-brand-brassDark hover:underline"
        >
          {t("mockExam.openTheEntity")} <Arrow />
        </Link>
      )}
      {/* Round 16 A26 -- a refused ranked finding said so only once
          opened, so the four the Engine could not answer read like the
          rows it had nothing to add to. The sentence sits on the row. */}
      {!q.narrative && q.narrative_refused && (
        <p
          data-testid={`mock-exam-refused-${q.id}`}
          className="mt-1 text-xs text-amber-800"
        >
          {t("mockExam.row.refused")}
        </p>
      )}
      {open && (
        <div className="mt-2 space-y-2 border-t border-paper-2 pt-2">
          <p className="text-xs text-slate-600">{q.question}</p>
          <ul className="space-y-0.5">
            {q.evidence.map((e) => (
              <li key={e.id} className="font-mono text-xs text-slate-500">
                {e.id}: {e.text}
              </li>
            ))}
          </ul>
          {q.narrative && (
            /* Round 12 D4 -- a note whose figures its evidence does not
               support is printed under its OWN heading, not beside the
               clean ones. The thing that made these dangerous was never
               being wrong; it was being wrong under a line saying they
               had been checked. */
            <div
              data-testid={`mock-exam-narrative-${q.id}`}
              data-unsupported={
                (q.narrative.unsupported_figures?.length ?? 0) > 0 ? "true" : "false"
              }
              /* Round 13 F3 -- the whole of what makes a note worth
                 re-reading, not only its figures. */
              data-flagged={needsReview(q.narrative) ? "true" : "false"}
              className={
                needsReview(q.narrative)
                  ? "rounded border border-rag-amber/40 bg-amber-50 p-2"
                  : "rounded border border-brand-brass/30 bg-brand-brass/5 p-2"
              }
            >
              <p
                className={
                  needsReview(q.narrative)
                    ? "font-mono text-[10px] uppercase tracking-wide text-amber-800"
                    : "font-mono text-[10px] uppercase tracking-wide text-brand-brassDark"
                }
              >
                {needsReview(q.narrative)
                  ? t("mockExam.narrativeNeedsCorrection")
                  : t("mockExam.narrativeLabel")}
              </p>
              <p className="mt-1 text-sm text-slate-800">{q.narrative.note}</p>
              {/* Round 11 C4 -- one line saying what it adds that the
                  two lines above it do not, because on its own it
                  restates them in longer words. */}
              <p className="mt-1 text-xs italic text-slate-500">
                {t("mockExam.narrativeWhat")}
              </p>
              {(q.narrative.unsupported_figures?.length ?? 0) > 0 && (
                <p
                  data-testid={`mock-exam-unsupported-${q.id}`}
                  className="mt-1 text-xs font-medium text-amber-900"
                >
                  {t("mockExam.unsupportedFigures", {
                    count: q.narrative.unsupported_figures!.length,
                    figures: q.narrative.unsupported_figures!.join(", "),
                  })}
                </p>
              )}
              {/* ROUND 13 F3 -- the three a numeral check cannot see. */}
              {q.narrative.contradicts_verdict && (
                <p
                  data-testid={`mock-exam-contradiction-${q.id}`}
                  className="mt-1 text-xs font-medium text-amber-900"
                >
                  {t("mockExam.contradictsVerdict", {
                    phrase: q.narrative.contradicts_verdict,
                    verdict: t(`mockExam.status.${q.status}`, {
                      defaultValue: q.status,
                    }),
                  })}
                </p>
              )}
              {(q.narrative.reused_ratios?.length ?? 0) > 0 && (
                <p
                  data-testid={`mock-exam-reused-ratio-${q.id}`}
                  className="mt-1 text-xs font-medium text-amber-900"
                >
                  {t("mockExam.reusedRatio", {
                    ratios: q.narrative.reused_ratios!.join("; "),
                  })}
                </p>
              )}
              {q.narrative.invented_method && (
                <p
                  data-testid={`mock-exam-invented-method-${q.id}`}
                  className="mt-1 text-xs font-medium text-amber-900"
                >
                  {t("mockExam.inventedMethod", {
                    phrase: q.narrative.invented_method,
                  })}
                </p>
              )}
              {(q.narrative.worse_uncited_evidence?.length ?? 0) > 0 && (
                <p
                  data-testid={`mock-exam-uncited-${q.id}`}
                  className="mt-1 text-xs font-medium text-amber-900"
                >
                  {t("mockExam.worseUncited", {
                    count: q.narrative.worse_uncited_evidence!.length,
                    ids: q.narrative.worse_uncited_evidence!.join(", "),
                  })}
                </p>
              )}
              {/* ROUND 13 F2 -- the evidence has moved since the note
                  was written. Not re-flagged: re-running the check
                  against today's evidence would call a note wrong that
                  was right when written, which says the wrong thing
                  about the Engine. What changed is the estate. */}
              {q.narrative.evidence_moved && (
                <p
                  data-testid={`mock-exam-evidence-moved-${q.id}`}
                  className="mt-1 text-xs text-slate-600"
                >
                  {t("mockExam.evidenceMoved", {
                    date: formatDateTime(q.narrative.checked_at ?? null, i18n.language),
                  })}
                </p>
              )}
              <p className="mt-1 text-xs text-slate-500">
                {t("mockExam.cites", {
                  ids: q.narrative.citations.join(", "),
                })}
                {q.narrative.invented_dropped > 0 &&
                  " · " +
                    t("mockExam.invented", {
                      count: q.narrative.invented_dropped,
                    })}
              </p>
              {/* D4 -- the one sentence that had to be said. "Grounded"
                  reads as "accurate" and means "cited", and the reader
                  who stops at it is the reader this is for. */}
              <p
                data-testid={`mock-exam-cited-not-checked-${q.id}`}
                className="mt-1 text-xs text-slate-500"
              >
                {t("mockExam.citedNotChecked")}
              </p>
            </div>
          )}
        </div>
      )}
    </li>
  );
}

export function MockExamView() {
  const { t, i18n } = useTranslation();
  const qc = useQueryClient();
  const toast = useToast();
  const [showAll, setShowAll] = useState(false);
  const [showUnassessed, setShowUnassessed] = useState(false);

  const runs = useQuery({
    queryKey: ["admin", "mock-exam", "runs"],
    queryFn: () => api.get<EngineBatchRun[]>("/v1/admin/mock-exam/runs"),
    refetchInterval: (query) => {
      const active = (query.state.data ?? []).some(
        (r) => r.status === "queued" || r.status === "running",
      );
      return active ? 2_000 : false;
    },
  });
  const active = (runs.data ?? []).find(
    (r) => r.status === "queued" || r.status === "running",
  );

  // D5 -- the page that launched the run never showed its result.
  //
  // The key carried `active ? "live" : "settled"`, so when a run
  // finished the key flipped BACK to "settled" -- an entry React Query
  // had already cached from the first page load, before the run. The
  // screen still read "0 Engine-narrated" half a minute later and only
  // a fresh visit showed 29. One key, and an explicit refetch at the
  // moment the run leaves.
  /* Round 19 A41 -- a past run read as it stood, from what it froze
     when it ran; null reads today's estate. */
  const [asRun, setAsRun] = useState<string | null>(null);
  const report = useQuery({
    queryKey: ["admin", "mock-exam", "report", asRun],
    queryFn: () =>
      api.get<MockExamReport>(
        asRun
          ? `/v1/admin/mock-exam/report?run_id=${encodeURIComponent(asRun)}`
          : "/v1/admin/mock-exam/report",
      ),
  });
  const pastRuns = (runs.data ?? []).filter(
    (r) => r.status === "succeeded" && r.finished_at,
  );
  const readingRun = pastRuns.find((r) => r.id === asRun) ?? null;

  const activeId = active?.id ?? null;
  const wasActive = useRef<string | null>(null);
  useEffect(() => {
    if (wasActive.current && !activeId) {
      // The run just finished. Show what it produced, here, rather
      // than leaving the operator to find out on another page.
      void qc.invalidateQueries({ queryKey: ["admin", "mock-exam", "report"] });
      const finished = (runs.data ?? []).find((r) => r.id === wasActive.current);
      toast.push({
        tone: finished?.status === "succeeded" ? "success" : "warning",
        message:
          finished?.status === "succeeded"
            ? t("mockExam.runDone", { n: finished.processed })
            : t("mockExam.runEnded", { status: finished?.status ?? "" }),
      });
    }
    wasActive.current = activeId;
  }, [activeId, qc, runs.data, t, toast]);

  const trigger = useMutation({
    mutationFn: () => api.post<EngineBatchRun>("/v1/admin/mock-exam/run", {}),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ["admin", "mock-exam"] });
      toast.push({ tone: "info", message: t("mockExam.queued") });
    },
    onError: (e: { status?: number }) =>
      toast.push({
        tone: e.status === 503 ? "warning" : "error",
        message: e.status === 503 ? t("mockExam.engineOff") : t("mockExam.failed"),
      }),
  });
  const pack = useMutation({
    mutationFn: () => downloadPack(asRun),
    onSuccess: () => toast.push({ tone: "success", message: t("mockExam.packDone") }),
    onError: () => toast.push({ tone: "error", message: t("mockExam.packFailed") }),
  });

  const rep = report.data;
  /* Round 12 F6 -- ranked likely findings the Engine has NOT narrated,
     counted here rather than inferred by a reader from the gap between
     "20 findings" and "29 narrated". They are the questions the
     deterministic script derived after the last run, and six of them
     were HIGH. */
  const engineRefusedCount = (rep?.likely_findings ?? []).filter(
    (q) => !q.narrative && q.narrative_refused,
  ).length;
  // A10 -- "derived since the last run" only for rows the run never saw.
  const unnarrated =
    (rep?.likely_findings ?? []).filter((q) => !q.narrative).length -
    engineRefusedCount;
  /* Round 11 C4 -- who may compose, and whether anything has been.
     The endpoints behind this page are admin-gated, so a preparer
     reaching it was offered two live buttons under a sentence saying
     her account cannot compose the report. A button that cannot work
     is the defect this programme has spent four rounds removing from
     the ceremonies. */
  const refused =
    report.isError && (report.error as { status?: number } | null)?.status === 403;
  const composed = Boolean(rep);
  /* ... and the denominator, from the first paint. It read
     "Drafting examiner notes... 0 / ..." for thirty-five seconds
     because the run's own total is not set until the driver starts
     ticking. The question count is known before the run begins. */
  const questionCount = rep?.summary.questions ?? null;
  const lastRunTotal = (runs.data ?? []).find((r) => r.total)?.total ?? null;
  return (
    <article data-testid="mock-exam-view" className="max-w-4xl space-y-5">
      <header className="space-y-1">
        <h1 className="font-display text-2xl text-slate-900">
          <MarsadIcon
            name="obligation"
            className="me-2 inline-block h-6 w-6 align-[-4px]"
          />
          {t("mockExam.heading")}
        </h1>
        <p className="max-w-3xl text-sm text-slate-500">{t("mockExam.body")}</p>
      </header>

      <section className="flex flex-wrap items-center gap-3 rounded-xl border border-paper-2 bg-white p-4 shadow-card">
        <button
          type="button"
          onClick={() => trigger.mutate()}
          disabled={trigger.isPending || Boolean(active) || refused}
          title={refused ? t("mockExam.refusedWhy") : undefined}
          data-testid="mock-exam-run"
          className="rounded bg-brand-brass px-3 py-1.5 text-sm font-medium text-brand-navy hover:bg-brand-brassDark disabled:opacity-50"
        >
          {t("mockExam.run")}
        </button>
        {active && (
          <span data-testid="mock-exam-progress" className="text-sm text-slate-600">
            {t("mockExam.progress", {
              processed: active.processed,
              total: active.total ?? questionCount ?? "…",
            })}
          </span>
        )}
        {rep?.narrative_run && !active && (
          /* Round 11 C4 -- the product's name for the thing that wrote
             it, and a date in the format every neighbour on the page
             uses. This read "Last narrated by stub-engine at
             2026-09-16T14:44" beside rows printing "16 Sept 2026,
             14:44". The model is kept because it is real provenance --
             beside the name, not instead of it. */
          <span data-testid="mock-exam-last-run" className="text-xs text-slate-500">
            {t("mockExam.lastRun", {
              model: rep.narrative_run.model
                ? t("mockExam.byModel", { model: rep.narrative_run.model })
                : "",
              date: formatDateTime(rep.narrative_run.finished_at, i18n.language),
            })}
            {/* ROUND 13 F4 -- what the deployment ASKED for, beside
                what answered. The composer has computed this since
                round 12 and this view did not read it, so "the mock
                exam prints the disagreement" was never satisfied --
                and the disagreeing case is the interesting one: two of
                four runs on the measured estate shared 32 of 32
                response digests under two different model names. */}
            {rep.narrative_run.model_disagrees && (
              <span
                data-testid="mock-exam-model-disagrees"
                className="ms-2 rounded bg-amber-100 px-1.5 py-0.5 text-[11px] font-medium text-amber-900"
              >
                {" "}
                {/* Round 17 A32 -- the examiner's sentence, so a replay
                    reads as a replay on both seats. */}
                {modelsDifferSentence(
                  t,
                  rep.narrative_run.model ?? "",
                  rep.narrative_run.configured_model ?? "",
                )}
              </span>
            )}
            {/* And when the name is the configuration repeated rather
                than a reading, which is a different claim. */}
            {rep.narrative_run.model && rep.narrative_run.model_observed === false && (
              <span
                data-testid="mock-exam-model-not-observed"
                className="ms-2 text-[11px] text-slate-500"
              >
                {t("mockExam.modelNotObserved")}
              </span>
            )}
          </span>
        )}
        <button
          type="button"
          onClick={() => pack.mutate()}
          /* Round 11 C4 -- pressed before a report exists it produced
             no download, no toast and no change, which is the worst of
             the three: the operator cannot tell whether it worked. */
          disabled={pack.isPending || !composed}
          title={
            refused
              ? t("mockExam.refusedWhy")
              : composed
                ? undefined
                : t("mockExam.packWhyDisabled")
          }
          data-testid="mock-exam-pack"
          className="ms-auto rounded border border-brand-brass/40 bg-brand-brass/5 px-3 py-1.5 text-sm font-medium text-brand-brassDark hover:bg-brand-brass/10 disabled:opacity-50"
        >
          {t("mockExam.pack")}
        </button>
      </section>

      {pastRuns.length > 0 && (
        <section
          data-testid="mock-exam-as-run"
          className="flex flex-wrap items-center gap-2 text-xs text-slate-600"
        >
          <label className="flex items-center gap-2">
            {t("mockExam.readAs")}
            <select
              data-testid="mock-exam-as-run-select"
              value={asRun ?? ""}
              onChange={(e) => setAsRun(e.target.value || null)}
              className="rounded border border-slate-300 bg-white px-2 py-1 text-xs"
            >
              <option value="">{t("mockExam.readToday")}</option>
              {pastRuns.map((r) => (
                <option key={r.id} value={r.id}>
                  {t("mockExam.readRun", {
                    date: formatDateTime(r.finished_at ?? "", i18n.language),
                  })}
                </option>
              ))}
            </select>
          </label>
          {readingRun && (
            <span data-testid="mock-exam-as-run-note">
              {t("mockExam.asRunNote", {
                date: formatDateTime(readingRun.finished_at ?? "", i18n.language),
              })}
            </span>
          )}
        </section>
      )}

      {/* Round 10 F4 -- the page rendered a heading and a button and
          nothing else until the report arrived, and said nothing at
          all if it never did. Every endpoint behind it is admin-gated,
          so the silent case is not rare: it is what a non-admin sees
          on a page they can reach. */}
      {/* Round 11 C4 -- what is being composed, and what already
          exists. An administrator arriving cold read "Composing the
          examination report..." and nothing else, on an estate that
          already carried a narrated run. A wait with no shape is
          indistinguishable from a page that is broken. */}
      {report.isLoading && (
        <div data-testid="mock-exam-loading" className="space-y-2">
          <p className="text-sm text-slate-500">
            {questionCount !== null
              ? t("mockExam.loadingCount", { count: questionCount })
              : t("mockExam.loading")}
          </p>
          {lastRunTotal !== null && (
            <p data-testid="mock-exam-loading-prior" className="text-xs text-slate-500">
              {t("mockExam.loadingPrior", { count: lastRunTotal })}
            </p>
          )}
          <div className="space-y-1.5" aria-hidden="true">
            {[0, 1, 2].map((i) => (
              <div key={i} className="h-7 animate-pulse rounded bg-paper-2" />
            ))}
          </div>
        </div>
      )}
      {report.isError && (
        <p
          data-testid="mock-exam-error"
          className="rounded border border-amber-300 bg-amber-50 px-3 py-2 text-sm text-amber-900"
        >
          {(report.error as { status?: number } | null)?.status === 403
            ? t("mockExam.refused")
            : t("mockExam.error")}
        </p>
      )}

      {rep && (
        <>
          <section
            data-testid="mock-exam-summary"
            className="flex flex-wrap gap-4 text-sm text-slate-700"
          >
            <span>
              {/* Round 19 B27 -- "4 likely finding(s) of 26 questions" read
                  as 22 passing, 21 of them not assessed and unseen. */}
              {rep.summary.not_assessed != null && rep.summary.not_assessed > 0
                ? t("mockExam.summary.flaggedWithUnassessed", {
                    flagged: rep.summary.flagged,
                    notAssessed: rep.summary.not_assessed,
                    passing: rep.summary.passing ?? 0,
                    total: rep.summary.questions,
                  })
                : t("mockExam.summary.flagged", {
                    flagged: rep.summary.flagged,
                    total: rep.summary.questions,
                  })}
              {typeof rep.narrative_run?.questions_asked === "number" &&
                rep.narrative_run.questions_asked !== rep.summary.questions && (
                  <span data-testid="mock-exam-asked-by-run">
                    {" ("}
                    {t("mockExam.summary.askedByRun", {
                      asked: rep.narrative_run.questions_asked,
                    })}
                    {")"}
                  </span>
                )}
            </span>
            <span className="text-rose-700">
              {t("mockExam.summary.high", { count: rep.summary.high })}
            </span>
            <span className="text-brand-brassDark">
              {t("mockExam.summary.narrated", {
                count: rep.summary.narrated,
              })}
            </span>
            {/* Round 12 F6 -- the page read "20 likely finding(s) of 32
                questions · 16 high · 29 Engine-narrated" while six of
                the twenty ranked likely findings, EVERY ONE OF THEM
                HIGH, carried no note at all: the six questions the
                deterministic script had derived since the run narrated
                an hour and twenty minutes earlier. Nothing said so, and
                a reader counting notes would conclude the Engine had
                declined to comment on the six worst rows.

                The machine-readable report was honest about it and the
                pack's proof excluded them. Only the screen was silent. */}
            {unnarrated > 0 && (
              <span data-testid="mock-exam-unnarrated" className="text-amber-800">
                {t("mockExam.summary.unnarrated", { count: unnarrated })}
              </span>
            )}
            {engineRefusedCount > 0 && (
              <span data-testid="mock-exam-refused" className="text-amber-800">
                {t("mockExam.summary.refused", { count: engineRefusedCount })}
              </span>
            )}
            {rep.summary.notes_with_unsupported_figures ? (
              <span
                data-testid="mock-exam-unsupported-count"
                className="text-amber-800"
              >
                {t("mockExam.summary.unsupported", {
                  count: rep.summary.notes_with_unsupported_figures,
                })}
              </span>
            ) : null}
          </section>

          <section className="space-y-2">
            <h2 className="text-sm font-semibold uppercase tracking-wide text-slate-500">
              {t("mockExam.findings")}
            </h2>
            {rep.likely_findings.length === 0 ? (
              <p className="text-sm text-emerald-700">{t("mockExam.clean")}</p>
            ) : (
              <>
                {/* Round 12 F7 -- "A field examination would raise this
                    as a named finding." printed sixteen times in one
                    screenful. A sentence true of every row is a
                    property of the list, so it is said once. */}
                <p
                  data-testid="mock-exam-findings-note"
                  className="text-xs text-slate-500"
                >
                  {t("mockExam.findingsNote")}
                </p>
                {/* I2 -- same rule as the line above: true of every
                    narrated note, so said once about the list. */}
                <EngineLanguageNote />
                <ul data-testid="mock-exam-findings" className="space-y-1">
                  {rep.likely_findings.map((q) => (
                    <QuestionRow key={q.id} q={q} />
                  ))}
                </ul>
              </>
            )}
          </section>

          <section className="space-y-2">
            <button
              type="button"
              onClick={() => setShowAll((v) => !v)}
              data-testid="mock-exam-toggle-all"
              className="text-sm text-brand-brassDark hover:underline"
            >
              {showAll ? t("mockExam.hideAll") : t("mockExam.showAll")}
            </button>
            {showAll && (
              <ul className="space-y-1">
                {rep.questions
                  .filter((q) => q.status === "ok")
                  .map((q) => (
                    <QuestionRow key={q.id} q={q} />
                  ))}
              </ul>
            )}
          </section>
          {(rep.summary.not_assessed ?? 0) > 0 && (
            <section className="space-y-2">
              <button
                type="button"
                onClick={() => setShowUnassessed((v) => !v)}
                data-testid="mock-exam-toggle-unassessed"
                className="text-sm text-brand-brassDark hover:underline"
              >
                {showUnassessed
                  ? t("mockExam.hideUnassessed")
                  : t("mockExam.showUnassessed", {
                      count: rep.summary.not_assessed ?? 0,
                    })}
              </button>
              {showUnassessed && (
                <ul data-testid="mock-exam-unassessed" className="space-y-1">
                  {rep.questions
                    .filter((q) => q.status === "not_assessed")
                    .map((q) => (
                      <QuestionRow key={q.id} q={q} />
                    ))}
                </ul>
              )}
            </section>
          )}
        </>
      )}
    </article>
  );
}
